import { useCallback } from "react";
import { format } from "date-fns";
import { OrderItem, OrderPayment, ApiRelatedOrderPayment } from "./ordersGridTypes.types";
import { GridValidRowModel } from "@mui/x-data-grid";
import { v4 as uuidv4 } from "uuid";
import { ApiRelatedOrderItem } from "./ordersGridTypes.types";
import { GridRowModel } from "@mui/x-data-grid";
import { FormatedField } from "../../types/fields.types";

export const calculateRowTotal = (row: OrderItem) => {
    return row.quantity * row.price - row.quantity * row.price * (row.discount / 100);
};

export const useCalculateOrder = () => {
    return useCallback((rows: GridValidRowModel) => {
        return rows.reduce((acumulater: number, current: OrderItem) => {
            return (acumulater = acumulater + calculateRowTotal(current));
        }, 0);
    }, []);
};

export const convertApiItemsToGridRows = (apiItems: ApiRelatedOrderItem[]) => {
    const items = apiItems.map((item: ApiRelatedOrderItem) => {
        const id = uuidv4();

        return {
            id: id,
            order_item_id: item.id,
            variantId: item.service_variant?.id,
            sku: item.service_variant?.sku,
            serviceName: item.service_variant?.service.name,
            agreementName: item.service_variant?.agreement.name,
            type: item.service_variant?.type,
            quantity: item.quantity,
            quantity_used: item.quantity_used,
            price: item.price,
            discount: item.discount,
        };
    });

    return items as GridRowModel[];
};

export const calculateApiItemsTotal = (apiItems: Array<ApiRelatedOrderItem>) => {
    const total = apiItems.reduce((accumulator: number, item: ApiRelatedOrderItem) => {
        const itemTotal = item.quantity * item.price - item.quantity * item.price * (item.discount / 100);
        return accumulator + itemTotal;
    }, 0);

    return total;
};

export const calculateTotalOfOrders = (orders: Array<any>) => {
    const total = orders.reduce((accumulator, currentOrder) => {
        const orderTotal = calculateApiItemsTotal(currentOrder.order_items);
        const newTotal = accumulator + orderTotal;

        return newTotal;
    }, 0);

    return total;
};

export const calculateTotalOfOrdersFromPayments = (orders: Array<any>, paymentMethod?: string) => {
    const total = orders.reduce((accumulator, currentOrder) => {
        let orderTotal = 0;
        if (currentOrder.orders_payments && Array.isArray(currentOrder.orders_payments)) {
            orderTotal = currentOrder.orders_payments.reduce((acc: number, payment: any) => {
                if (paymentMethod && payment.payment_method !== paymentMethod) return acc;
                return acc + (Number(payment.amount) || 0);
            }, 0);
        }
        return accumulator + orderTotal;
    }, 0);

    return total;
};

export type GroupedOrdersTotalsByPaymentMethod = {
    [key: string]: number;
};

export const getTodayOrdersGroupedByPaymentMethod = (
    orders: Array<any>,
    paymentMethodField: FormatedField,
): GroupedOrdersTotalsByPaymentMethod => {
    // Step 1: Create a simple map of payment method values to their display labels
    const paymentMethodLabels: Record<string, string> = {};
    paymentMethodField.settings?.options?.choices?.forEach((choice) => {
        if (choice.value && choice.text) {
            paymentMethodLabels[choice.value] = choice.text;
        }
    });

    // Step 2: Iterate over orders and their payments to calculate totals
    const totalsPerPaymentMethod: Record<string, number> = {};

    orders.forEach((order) => {
        if (order.orders_payments && Array.isArray(order.orders_payments)) {
            order.orders_payments.forEach((payment: any) => {
                const paymentMethod = payment.payment_method;
                const amount = Number(payment.amount) || 0;

                if (paymentMethod) {
                    if (!totalsPerPaymentMethod[paymentMethod]) {
                        totalsPerPaymentMethod[paymentMethod] = 0;
                    }
                    totalsPerPaymentMethod[paymentMethod] += amount;
                }
            });
        }
    });

    // Step 3: Create the final object with readable labels
    const result: GroupedOrdersTotalsByPaymentMethod = {};
    Object.entries(totalsPerPaymentMethod).forEach(([paymentMethod, total]) => {
        const readableLabel = paymentMethodLabels[paymentMethod] || paymentMethod;
        result[readableLabel] = total;
    });

    return result;
};

export const calculatePaymentsTotal = (payments: Array<OrderPayment>) => {
    const total = payments.reduce((accumulator, payment) => {
        const paymentAmount = Number(payment.amount) || 0;
        return accumulator + paymentAmount;
    }, 0);

    return total;
};

export const getDefaultPaymentDate = () => {
    return format(new Date(), "yyyy-MM-dd");
};



export type PaymentValidation = {
    isValid: boolean;
    error?: string;
    difference?: number;
};

export const validatePayments = (payments: Array<OrderPayment>, orderTotal: number) => {
    const validation: PaymentValidation = {
        isValid: true,
    };

    if (payments.length === 0) {
        validation.isValid = false;
        validation.error = "Adicione pelo menos um método de pagamento";
        return validation;
    }

    const hasEmptyPaymentMethod = payments.some((payment) => !payment.payment_method);
    if (hasEmptyPaymentMethod) {
        validation.isValid = false;
        validation.error = "Todos os pagamentos devem ter um método de pagamento selecionado";
        return validation;
    }

    const paymentsTotal = calculatePaymentsTotal(payments);
    const difference = paymentsTotal - orderTotal;

    if (Math.abs(difference) > 0.01) {
        validation.isValid = false;
        validation.difference = difference;
        if (difference > 0) {
            validation.error = `O total dos pagamentos (€${paymentsTotal.toFixed(2)}) excede o total da compra (€${orderTotal.toFixed(2)}) em €${difference.toFixed(2)}`;
        } else {
            validation.error = `O total dos pagamentos (€${paymentsTotal.toFixed(2)}) é inferior ao total da compra (€${orderTotal.toFixed(2)}) em €${Math.abs(difference).toFixed(2)}`;
        }
        return validation;
    }

    return validation;
};

export const convertApiPaymentsToRows = (apiPayments: Array<ApiRelatedOrderPayment>) => {
    const payments = apiPayments.map((payment) => {
        const paymentRow: OrderPayment = {
            id: uuidv4(),
            order_payment_id: payment.id,
            payment_method: payment.payment_method,
            amount: payment.amount,
            payment_date: payment.payment_date,
        };
        return paymentRow;
    });

    return payments;
};
